import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { formatDateDisplay, formatTimeOfDay } from '../../utils/date';
import { meetingModeLabel, normalizeMeetingMode, type SignupMeetingMode } from '../../utils/signupSlots';

interface SignupEvent {
  id: string;
  title: string;
  description: string | null;
  start_date: string;
  end_date: string;
  slot_duration_minutes: number;
  max_signups_per_slot: number;
  is_active: boolean;
  created_at: string;
  signup_slots?: {
    id: string;
    slot_date: string;
    start_time: string;
    end_time: string;
    meeting_mode: string | null;
    is_available: boolean;
    created_at: string;
    signup_registrations: SignupRegistration[];
  }[];
}



interface SignupRegistration {
  id: string;
  slot_id: string;
  parent_name: string;
  parent_email: string;
  student_name: string;
  phone: string | null;
  notes: string | null;
  created_at: string;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  eventId: string;
  slotId: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  registrations: SignupRegistration[];
  isAvailable: boolean;
  meetingMode: SignupMeetingMode | null;
  slotDuration: number;
  maxSignups: number;
}

const localizer = momentLocalizer(moment);

interface SignupCalendarProps {
  eventId?: string; // Optional: if provided, only show slots for this specific event
  includeInactiveEvents?: boolean; // Optional: show inactive events too (default false)
}

export function SignupCalendar({ eventId, includeInactiveEvents = false }: SignupCalendarProps) {
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640 || /Mobi|Android/i.test(navigator.userAgent));
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch signup events and slots
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['signup-events-for-calendar', eventId, includeInactiveEvents ? 'all' : 'active'],
    queryFn: async () => {
      let query = supabase
        .from('signup_events')
        .select(`
          *,
          signup_slots (
            id,
            slot_date,
            start_time,
            end_time,
            meeting_mode,
            is_available,
            signup_registrations (
              id,
              parent_name,
              parent_email,
              student_name,
              phone,
              notes,
              created_at
            )
          )
        `)
        ;

      if (!includeInactiveEvents) {
        query = query.eq('is_active', true);
      }

      if (eventId) {
        query = query.eq('id', eventId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SignupEvent[];
    },
  });

  // Convert events and slots to calendar events (memoized to avoid render loops)
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    return events.flatMap((event) =>
      event.signup_slots?.map((slot) => {
        const startDateTime = moment(`${slot.slot_date}T${slot.start_time}`);
        const endDateTime = moment(`${slot.slot_date}T${slot.end_time}`);
        const registrationCount = slot.signup_registrations?.length || 0;

        // Generate title based on registration status
        let title = event.title;
        if (registrationCount === 0) {
          title += ' - Free';
        } else {
          const registrationDetails = slot.signup_registrations
            ?.map((reg) => `${reg.student_name} (${reg.parent_name})`)
            .join(', ');
          title += ` - ${registrationCount}/${event.max_signups_per_slot} registered: ${registrationDetails}`;
        }

        const meetingMode = normalizeMeetingMode(slot.meeting_mode);
        if (meetingMode) {
          title += ` · ${meetingModeLabel(meetingMode)}`;
        }

        return {
          id: `${event.id}-${slot.id}`,
          title,
          start: startDateTime.toDate(),
          end: endDateTime.toDate(),
          eventId: event.id,
          slotId: slot.id,
          slotDate: slot.slot_date,
          startTime: slot.start_time,
          endTime: slot.end_time,
          registrations: slot.signup_registrations || [],
          isAvailable: slot.is_available,
          meetingMode: normalizeMeetingMode(slot.meeting_mode),
          slotDuration: event.slot_duration_minutes,
          maxSignups: event.max_signups_per_slot,
        };
      }) || []
    );
  }, [events]);

  const { defaultDate, minTime, maxTime, scrollToTime } = useMemo(() => {
    const fallbackMin = moment().startOf('day').add(6, 'hours').toDate();
    const fallbackMax = moment().startOf('day').add(22, 'hours').toDate();
    const fallbackScroll = moment().startOf('day').add(9, 'hours').add(30, 'minutes').toDate();

    if (calendarEvents.length === 0) {
      return {
        defaultDate: undefined as Date | undefined,
        minTime: fallbackMin,
        maxTime: fallbackMax,
        scrollToTime: fallbackScroll,
      };
    }

    // Find earliest slot event
    let earliest = calendarEvents[0].start;
    for (const e of calendarEvents) {
      if (e.start < earliest) earliest = e.start;
    }

    const earliestDay = moment(earliest).startOf('day');
    const earliestDayKey = earliestDay.format('YYYY-MM-DD');

    const sameDay = calendarEvents.filter(
      (e) => moment(e.start).format('YYYY-MM-DD') === earliestDayKey
    );

    if (sameDay.length === 0) {
      return {
        defaultDate: earliestDay.toDate(),
        minTime: fallbackMin,
        maxTime: fallbackMax,
        scrollToTime: fallbackScroll,
      };
    }

    const toMinutes = (d: Date) => d.getHours() * 60 + d.getMinutes();
    let minMinutes = toMinutes(sameDay[0].start);
    let maxMinutes = toMinutes(sameDay[0].end);
    for (const e of sameDay) {
      minMinutes = Math.min(minMinutes, toMinutes(e.start));
      maxMinutes = Math.max(maxMinutes, toMinutes(e.end));
    }

    // Snap to 30-minute boundaries so the gutter doesn't show odd minute values.
    const roundDownTo30 = (m: number) => Math.floor(m / 30) * 30;
    const roundUpTo30 = (m: number) => Math.ceil(m / 30) * 30;
    minMinutes = roundDownTo30(minMinutes);
    maxMinutes = roundUpTo30(maxMinutes);

    // Ensure we show at least 8 hours (480 minutes)
    const minWindowMinutes = 8 * 60;
    const duration = maxMinutes - minMinutes;
    if (duration < minWindowMinutes) {
      const needed = minWindowMinutes - duration;
      const extendBefore = Math.floor(needed / 2);
      const extendAfter = needed - extendBefore;
      minMinutes = Math.max(0, minMinutes - extendBefore);
      maxMinutes = Math.min(24 * 60, maxMinutes + extendAfter);

      // If we hit bounds, enforce minimum window by shifting the other side.
      if (maxMinutes - minMinutes < minWindowMinutes) {
        if (minMinutes === 0) {
          maxMinutes = Math.min(24 * 60, minWindowMinutes);
        } else if (maxMinutes === 24 * 60) {
          minMinutes = Math.max(0, 24 * 60 - minWindowMinutes);
        }
      }
    }

    const base = moment().startOf('day');
    const computedMin = base.clone().add(minMinutes, 'minutes').toDate();
    const computedMax = base.clone().add(maxMinutes, 'minutes').toDate();

    return {
      defaultDate: earliestDay.toDate(),
      minTime: computedMin,
      maxTime: computedMax,
      scrollToTime: computedMin,
    };
  }, [calendarEvents]);

  const eventStyleGetter = (event: CalendarEvent) => {
    const registrationCount = event.registrations.length;
    const maxSignups = event.maxSignups;
    const availabilityRatio = registrationCount / maxSignups;

    let backgroundColor = '#e2e8f0'; // Default gray
    let borderColor = '#cbd5e1';

    if (availabilityRatio >= 1) {
      // Fully booked
      backgroundColor = '#fecaca'; // Red
      borderColor = '#f87171';
    } else if (registrationCount > 0) {
      // Some registrations
      backgroundColor = '#dbeafe'; // Blue
      borderColor = '#93c5fd';
    } else {
      // No registrations (Free)
      backgroundColor = '#f3f4f6'; // Light gray
      borderColor = '#d1d5db';
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: '#1f2937',
        border: '2px solid',
        display: 'block',
        fontSize: isMobile ? '10px' : '12px',
      }
    };
  };

  if (eventsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">
          {includeInactiveEvents ? 'No signup events found.' : 'No active signup events found.'}
        </p>
      </div>
    );
  }

  return (
    <div className={isMobile ? 'p-1' : 'p-4'}>
      <div className={isMobile ? 'bg-white p-2 rounded-lg shadow' : 'bg-white p-6 rounded-lg shadow'}>
        {/* Legend */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Calendar Legend</h3>
          <div className="flex flex-wrap gap-3 text-xs">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: '#fecaca', border: '1px solid #f87171' }}></div>
              <span>Fully Booked</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: '#dbeafe', border: '1px solid #93c5fd' }}></div>
              <span>Some Registrations</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded mr-2" style={{ backgroundColor: '#f3f4f6', border: '1px solid #d1d5db' }}></div>
              <span>Free</span>
            </div>
          </div>
        </div>

        {/* Selected Event Details */}
        {selectedEvent && (
          <div className={isMobile ? 'mb-2 p-2 bg-gray-50 rounded-lg text-xs' : 'mb-4 p-4 bg-gray-50 rounded-lg'}>
            <div className="flex justify-between">
              <h3 className={isMobile ? 'text-base font-semibold' : 'text-lg font-semibold'}>
                {selectedEvent.title}
              </h3>
              <button 
                onClick={() => setSelectedEvent(null)} 
                className="text-gray-500 hover:text-gray-700 text-lg"
              >
                ×
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <div>
                <p><strong>Date:</strong> {formatDateDisplay(selectedEvent.slotDate)}</p>
                <p><strong>Time:</strong> {formatTimeOfDay(selectedEvent.startTime, { hour12: true })} - {formatTimeOfDay(selectedEvent.endTime, { hour12: true })}</p>
                <p><strong>Duration:</strong> {selectedEvent.slotDuration} minutes</p>
                {selectedEvent.meetingMode && (
                  <p><strong>Held:</strong> {meetingModeLabel(selectedEvent.meetingMode)}</p>
                )}
              </div>
              <div>
                <p><strong>Registrations:</strong> {selectedEvent.registrations.length === 0 ? 'Free' : `${selectedEvent.registrations.length}/${selectedEvent.maxSignups} registered`}</p>
                <p><strong>Status:</strong> {selectedEvent.isAvailable ? 'Available' : 'Unavailable'}</p>
              </div>
            </div>
            
            {/* Registration Details */}
            {selectedEvent.registrations.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="font-medium mb-2">Registrations:</p>
                <div className="space-y-1">
                  {selectedEvent.registrations.map((reg) => (
                    <div key={reg.id} className="text-xs bg-white p-2 rounded border">
                      <p><strong>{reg.student_name}</strong> - {reg.parent_name}</p>
                      <p className="text-gray-600">{reg.parent_email}</p>
                      {reg.notes && <p className="text-gray-500 italic">Note: {reg.notes}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Calendar */}
        <div style={{ height: isMobile ? '500px' : '800px' }}>
          <BigCalendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            eventPropGetter={eventStyleGetter}
            onSelectEvent={(event) => setSelectedEvent(event)}
            views={['month', 'week', 'day']}
            defaultView="day"
            defaultDate={defaultDate}
            formats={{
              timeGutterFormat: (date) => {
                // Avoid relying on `localizer` here (can be undefined per typings).
                // Show only hour on :00 and include minutes on :30.
                const minutes = date.getMinutes();
                const hours = date.getHours();
                const suffix = hours >= 12 ? 'PM' : 'AM';
                const hour12Val = hours % 12 === 0 ? 12 : hours % 12;
                return minutes === 0
                  ? `${hour12Val} ${suffix}`
                  : `${hour12Val}:${String(minutes).padStart(2, '0')} ${suffix}`;
              },
            }}
            toolbar={true}
            step={30}
            timeslots={1}
            min={minTime}
            max={maxTime}
            scrollToTime={scrollToTime}
          />
        </div>
      </div>
    </div>
  );
}
